import React, { useState } from 'react';
import { ChevronDown, ChevronUp } from 'lucide-react';
import { formatTextWithLinks } from '../../utils/formatters';

interface ExpandableTextProps {
  text: string;
  maxLength?: number;
  className?: string;
  maxHeight?: string;
}

/**
 * A component that displays text in a space-efficient way,
 * with the ability to expand to show the full content.
 * Automatically converts URLs to clickable links.
 * Provides hover functionality to see the full text.
 */
export default function ExpandableText({ 
  text, 
  maxLength = 50, 
  className = '',
  maxHeight = '4.5rem' // Approximately 3 lines of text
}: ExpandableTextProps) {
  const [isExpanded, setIsExpanded] = useState(false);
  const shouldTruncate = text.length > maxLength;

  // If the text is short enough, just display it
  if (!shouldTruncate) {
    return <div className={className} title={text}>{formatTextWithLinks(text)}</div>;
  }

  return (
    <div className={`relative ${className}`}>
      <div 
        className={`overflow-hidden transition-all duration-200 ${isExpanded ? '' : 'line-clamp-3'}`}
        style={{ maxHeight: isExpanded ? '100rem' : maxHeight }}
        title={!isExpanded ? text : undefined}
      >
        {formatTextWithLinks(text)}
      </div>
      
      {shouldTruncate && (
        <button
          onClick={() => setIsExpanded(!isExpanded)}
          className="mt-1 text-xs text-primary hover:text-primary-dark flex items-center"
          aria-label={isExpanded ? "Show less" : "Show more"}
        >
          {isExpanded ? (
            <>
              <ChevronUp className="h-3 w-3 mr-1" />
              Show less
            </>
          ) : (
            <>
              <ChevronDown className="h-3 w-3 mr-1" />
              Show more
            </>
          )}
        </button>
      )}
    </div>
  );
}
